// FILE: src/services/coverageService.js
//
// Domain coverage ("question-bank health") aggregation for the Admin Dashboard.
//
// For each ACTIVE certification we compare how many active (published) questions
// exist in each domain against a blueprint-weighted target, expressed as a
// "depth %" with a colour band:
//
//   target_per_domain = DEPTH_TARGET_MULTIPLIER
//                       × fullExam.totalQuestions
//                       × (categoryDistribution[domain] / 100)
//
//   depthPct = round(activeCount / target × 100)        (0 when target is 0)
//
// The blueprint weights (categoryDistribution) and full-exam length both live in
// src/config/exams.js, so adding or retuning a cert needs no change here. The set
// of certs shown is driven by getActiveCertIds(), so a new cert appears
// automatically once it is no longer `comingSoon`.
//
// "Active only" means question.status === 'active' — the lifecycle value that
// makes a question eligible for learner-facing sessions (see lifecycleConstants).

import { getExamConfig, getActiveCertIds } from '../config/exams';
import { EXAM_CATEGORIES } from '../config/examCategories';

/**
 * How many full-exam's-worth of active questions constitutes a "fully stocked"
 * bank (depth = 100%). 3 ≈ enough to assemble ~3 non-repeating full exams.
 * Tune globally here, or per-cert via DEPTH_TARGET_MULTIPLIER_BY_EXAM.
 */
export const DEPTH_TARGET_MULTIPLIER = 3;

/** Optional per-cert overrides for the depth multiplier (examId -> number). */
export const DEPTH_TARGET_MULTIPLIER_BY_EXAM = {};

/** Colour bands for depth %, matching the dashboard legend. */
export const COVERAGE_BANDS = [
  { key: 'critical', max: 60,       color: '#c62828', label: '<60' },   // red
  { key: 'low',      max: 80,       color: '#e07b00', label: '60–79' }, // orange
  { key: 'good',     max: 100,      color: '#1565c0', label: '80–99' }, // blue
  { key: 'full',     max: Infinity, color: '#2e7d32', label: '100+' },  // green
];

/** Returns the band object for a given depth percentage. */
export function bandFor(pct) {
  const v = Number.isFinite(pct) ? pct : 0;
  return COVERAGE_BANDS.find((b) => v < b.max) || COVERAGE_BANDS[COVERAGE_BANDS.length - 1];
}

/**
 * Short, heatmap-friendly labels for long category names. Falls back to the
 * raw category when no abbreviation is defined.
 */
export const SHORT_CATEGORY_LABELS = {
  // PMP process groups
  'Initiating': 'Init',
  'Planning': 'Plan',
  'Execution': 'Exec',
  'Monitoring and Controlling': 'M&C',
  'Closing': 'Close',
  // CPMAI phases
  'Business Understanding': 'Biz Und',
  'Data Understanding': 'Data Und',
  'Data Preparation': 'Prep',
  'Model Development': 'Modeling',
  'Model Evaluation': 'Eval',
  'Model Operationalization': 'Ops',
  // PMI-PMOCP domains
  'Organizational Development and Alignment': 'Org Dev',
  'PMO Strategic Elements': 'Strategy',
  'PMO Design and Structuring': 'Design',
  'PMO Operations and Performance': 'Ops',
  'PMO Enhancement and Effectiveness': 'Enhance',
  // PMI-ACP ECO domains
  'Mindset': 'Mind',
  'Leadership': 'Lead',
  'Product': 'Prod',
  'Delivery': 'Deliv',
};

/** Abbreviated label for a category (raw value if no abbreviation exists). */
export function shortLabel(category) {
  return SHORT_CATEGORY_LABELS[category] || category;
}

function multiplierFor(examId) {
  const v = DEPTH_TARGET_MULTIPLIER_BY_EXAM[examId];
  return Number.isFinite(v) && v > 0 ? v : DEPTH_TARGET_MULTIPLIER;
}

/**
 * Counts ACTIVE questions per category for one exam.
 * Questions with a missing status default to 'active' (consistent with the
 * dashboard and session-eligibility logic).
 * @param {object[]} questions normalized question objects (camelCase)
 * @returns {Object<string,number>}
 */
function activeCountsByCategory(questions) {
  const counts = {};
  for (const q of questions || []) {
    if ((q.status || 'active') !== 'active') continue;
    const cat = (q.category || '').trim();
    if (!cat) continue;
    counts[cat] = (counts[cat] || 0) + 1;
  }
  return counts;
}

/**
 * Computes blueprint-weighted coverage for a single cert.
 * @param {object[]} questions normalized question objects for this exam
 * @param {string}   examId
 * @returns {{ examId:string, displayName:string, cells:Array<{
 *   category:string, label:string, active:number, target:number,
 *   depthPct:number, color:string }> }}
 */
export function computeCertCoverage(questions, examId) {
  const cfg = getExamConfig(examId) || {};
  const weights = cfg.categoryDistribution || {};
  const fullTotal = (cfg.fullExam && cfg.fullExam.totalQuestions) || 0;
  const mult = multiplierFor(examId);

  // Domain order: prefer the canonical EXAM_CATEGORIES list; fall back to weights.
  const cats = (EXAM_CATEGORIES[examId] && EXAM_CATEGORIES[examId].length)
    ? EXAM_CATEGORIES[examId]
    : Object.keys(weights);

  const active = activeCountsByCategory(questions);

  const cells = cats.map((category) => {
    const weight = weights[category] || 0;
    const target = Math.round(mult * fullTotal * (weight / 100));
    const activeCount = active[category] || 0;
    const depthPct = target > 0 ? Math.round((activeCount / target) * 100) : 0;
    const gap = Math.max(0, target - activeCount);
    return {
      category,
      label: shortLabel(category),
      active: activeCount,
      target,
      depthPct,
      gap,
      color: bandFor(depthPct).color,
    };
  });

  const gapTotal = cells.reduce((sum, c) => sum + c.gap, 0);

  return { examId, displayName: cfg.displayName || examId, cells, gapTotal };
}

/**
 * Computes coverage rows for every ACTIVE cert (getActiveCertIds), in config
 * order. Certs without a defined category taxonomy are skipped.
 * @param {Object<string, object[]>} questionsByExam
 * @returns {Array} one row per cert (see computeCertCoverage)
 */
export function computeCoverageMatrix(questionsByExam) {
  return getActiveCertIds()
    .filter((id) => (EXAM_CATEGORIES[id] || []).length > 0)
    .map((id) => computeCertCoverage((questionsByExam && questionsByExam[id]) || [], id));
}

/**
 * Maximum number of domain columns across all coverage rows. Used to render a
 * fixed-width grid so domains line up vertically across certs.
 * @param {Array} rows  output of computeCoverageMatrix / computeCertCoverage[]
 * @returns {number}
 */
export function maxDomainColumns(rows) {
  return (rows || []).reduce((max, r) => Math.max(max, (r.cells || []).length), 0);
}

/** Maps a depth % to a CSS custom-property colour (for the AdminScreen canvas). */
export function bandVar(pct) {
  const key = bandFor(pct).key;
  return {
    critical: 'var(--red)',
    low:      'var(--orange)',
    good:     'var(--accent)',
    full:     'var(--green)',
  }[key];
}

/**
 * Formats a "bank fill-up" ETA from a remaining gap and a daily publish rate.
 *   - gap 0            -> "Fully stocked"
 *   - rate 0 / unknown -> "no recent QF runs"
 *   - else             -> "~N days" / "~N wk" / "~N mo"
 * @param {number} gapTotal questions still needed to reach target
 * @param {number} perDay   questions published per day (QF throughput)
 * @returns {{ etaDays:(number|null), text:string }}
 */
export function formatEta(gapTotal, perDay) {
  if (!gapTotal || gapTotal <= 0) return { etaDays: 0, text: 'Fully stocked' };
  if (!perDay || perDay <= 0) return { etaDays: null, text: 'no recent QF runs' };
  const days = Math.ceil(gapTotal / perDay);
  let text;
  if (days <= 10) text = `~${days} day${days === 1 ? '' : 's'}`;
  else if (days < 70) text = `~${Math.round(days / 7)} wk`;
  else text = `~${Math.round(days / 30)} mo`;
  return { etaDays: days, text };
}
